import { Button, Tag } from '@honestica/ui-kit/src';
import {
  CheckCircleFilledIcon,
  CheckOutlinedIcon,
  CloseCircleFilledIcon,
  CopyOutlinedIcon,
  ScissorOutlinedIcon,
  WarningFilledIcon,
} from '@honestica/ui-kit/src/icons';
import { UploadStatus } from '@launcher/context/DocumentProcessing.context';
import { Progress, Tooltip } from 'antd';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './DocumentImportCard.module.less';
import { DocumentProgress } from './DocumentImportStatus.component';

interface DocumentImportCardProps {
  document: DocumentProgress;
  isChild?: boolean;
  index?: number;
}

const getProgressFromStatus = (status: UploadStatus): number => {
  switch (status) {
    case 'PENDING':
      return 0;
    case 'UPLOADING':
      return 10;
    case 'ANALYZING':
      return 50;
    case 'TERMINATED':
    case 'ANALYZE_UNAVAILABLE':
    case 'FAILURE':
      return 100;
    default:
      return 0;
  }
};

const useProgressLabel = (status: UploadStatus): ReactNode => {
  const { t } = useTranslation();

  switch (status) {
    case 'PENDING':
      return t('documents.importStatus.card.pending');
    case 'UPLOADING':
      return (
        <>
          <span>{t('documents.importStatus.card.uploading')}</span>
          <span>{t('documents.importStatus.card.toAnalyze')}</span>
        </>
      );
    case 'ANALYZING':
      return (
        <>
          <span>{t('documents.importStatus.card.analyzing')}</span>
          <span>{t('documents.importStatus.card.almostDone')}</span>
        </>
      );
    case 'TERMINATED':
      return t('documents.importStatus.card.completed');
    case 'ANALYZE_UNAVAILABLE':
      return t('documents.importStatus.card.analyzeUnavailable');
    case 'FAILURE':
      return t('documents.importStatus.card.failure');
    default:
      return '';
  }
};

const COPY_TRANSITION_DURATION = 3000;

type CardVariant = 'error' | 'progress-ia' | 'progress' | 'success' | 'analyze-unavailable';

const getVariantFromStatus = (status: UploadStatus): CardVariant => {
  switch (status) {
    case 'FAILURE':
      return 'error';
    case 'ANALYZING':
      return 'progress-ia';
    case 'TERMINATED':
      return 'success';
    case 'ANALYZE_UNAVAILABLE':
      return 'analyze-unavailable';
    case 'UPLOADING':
    case 'PENDING':
    default:
      return 'progress';
  }
};

export const DocumentImportCard = ({
  document,
  isChild = false,
  index = 0,
}: DocumentImportCardProps) => {
  const { t } = useTranslation();
  const variant = getVariantFromStatus(document.status);
  const label = useProgressLabel(document.status);
  const [copied, setCopied] = useState(false);
  const [copiedError, setCopiedError] = useState(false);
  const [childrenExpanded, setChildrenExpanded] = useState(true);
  const timer = useRef<NodeJS.Timeout | undefined>(undefined);
  const errorTimer = useRef<NodeJS.Timeout | undefined>(undefined);

  const animationDelay = `${index * 0.15}s`;

  // Calculate progress
  const hasChildren = document.children && document.children.length > 0;
  const totalChildren = document.totalChildren || document.children?.length || 0;
  const completedChildren = totalChildren - (document.children?.length || 0);

  // For parent documents with children, progress from 50% to 100% based on completed children
  let progress = getProgressFromStatus(document.status);
  if (hasChildren && totalChildren > 0) {
    progress = 50 + (completedChildren / totalChildren) * 50;
  }

  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
      if (errorTimer.current !== undefined) clearTimeout(errorTimer.current);
    },
    [],
  );

  const handleCopy = () => {
    if (copied) return;
    navigator.clipboard.writeText(document.id);
    setCopied(true);
    timer.current = setTimeout(() => {
      setCopied(false);
    }, COPY_TRANSITION_DURATION);
  };

  const handleCopyError = () => {
    if (copiedError) return;
    const payload = JSON.stringify(
      {
        filename: document.name,
        errorCode: document.error?.code,
        httpStatusCode: document.error?.httpDetails?.statusCode ?? null,
        httpMessage: document.error?.httpDetails?.message ?? null,
        httpResponse: document.error?.httpDetails?.response ?? null,
        timestamp: new Date().toISOString(),
      },
      null,
      2,
    );
    navigator.clipboard.writeText(payload);
    setCopiedError(true);
    errorTimer.current = setTimeout(() => {
      setCopiedError(false);
    }, COPY_TRANSITION_DURATION);
  };

  const getErrorMessage = (errorCode: string, params?: Record<string, string | number>): string => {
    return t(`documents.job.error.${errorCode}`, params);
  };

  const CopyIcon = copied ? CheckOutlinedIcon : CopyOutlinedIcon;
  const CopyErrorIcon = copiedError ? CheckOutlinedIcon : CopyOutlinedIcon;

  const tooltipContent = copied ? (
    t('copied')
  ) : (
    <div>
      <div>
        {t('documents.importStatus.card.identifier')} {document.id}
      </div>
      <div>{t('documents.importStatus.card.copyTooltip')}</div>
    </div>
  );

  const errorTooltipContent = copiedError
    ? t('copied')
    : t('documents.importStatus.card.copyTooltip');

  // Only show copy icon when we have the real ID from backend (after upload success)
  const hasRealId =
    document.status === 'ANALYZING' ||
    document.status === 'TERMINATED' ||
    document.status === 'ANALYZE_UNAVAILABLE';

  return (
    <div className={styles.cardWrapper} data-is-child={isChild}>
      <div
        className={styles.card}
        data-variant={variant}
        data-testid="document-import-card"
        data-document-id={document.id}
        style={{ '--animation-delay': animationDelay } as React.CSSProperties}
      >
        <div className={styles.cardHeader}>
          <span className={styles.filename} data-testid="document-import-card-filename">
            {document.name}
          </span>
          {/*
            Keyed by status: the card itself stays mounted across a status change (that's the
            fix), but an open Tooltip has no other signal that its trigger may have moved to a
            new position in the list, so it would otherwise stay anchored to stale coordinates.
            Remounting just this Tooltip on status change closes/resets it, same as the old
            (incidental) full-card remount used to.
          */}
          <Tooltip key={`copy-${document.status}`} title={tooltipContent} mouseEnterDelay={0.5}>
            <CopyIcon
              className={styles.copyIcon}
              data-hidden={!hasRealId}
              aria-hidden={!hasRealId}
              onClick={hasRealId ? handleCopy : undefined}
              data-testid="document-import-card-copy"
            />
          </Tooltip>
          {variant === 'error' && (
            <Tooltip
              key={`copy-error-${document.status}`}
              title={errorTooltipContent}
              mouseEnterDelay={0.5}
            >
              <CopyErrorIcon
                className={styles.copyIcon}
                onClick={handleCopyError}
                data-testid="document-import-card-copy-error"
              />
            </Tooltip>
          )}
        </div>

        <div className={styles.progressWrapper} data-variant={variant}>
          <Progress
            percent={progress}
            showInfo={false}
            size="small"
            status={progress !== 100 ? 'active' : 'normal'}
            data-testid="document-import-card-progress"
          />
          {variant === 'success' && (
            <CheckCircleFilledIcon
              className={styles.successIcon}
              data-testid="document-import-card-success-icon"
            />
          )}
          {variant === 'error' && (
            <CloseCircleFilledIcon
              className={styles.errorIcon}
              data-testid="document-import-card-error-icon"
            />
          )}
          {variant === 'analyze-unavailable' && (
            <WarningFilledIcon
              className={styles.warningIcon}
              data-testid="document-import-card-warning-icon"
            />
          )}
        </div>

        <div className={styles.cardFooter} data-testid="document-import-card-status">
          {!hasChildren && !document.parentName && label}
          {!hasChildren && document.parentName && (
            <div className={styles.labelWithTag}>
              <span>{label}</span>
              <Tooltip
                key={document.status}
                title={t('documents.importStatus.card.originatedFrom', {
                  name: document.parentName,
                })}
              >
                <Tag className={styles.publipostageTag}>
                  <ScissorOutlinedIcon className={styles.scissorIcon} />
                  {t('documents.importStatus.card.publipostageTag')}
                </Tag>
              </Tooltip>
            </div>
          )}
          {hasChildren && (
            <>
              <span>{t('documents.importStatus.card.documentBurst')}</span>
              <div className={styles.documentBurstInfo}>
                <span>
                  → {t('documents.importStatus.card.splitInto', { count: totalChildren })}
                </span>
                <Tag color="purple">
                  <ScissorOutlinedIcon className={styles.scissorIcon} />
                  {`${completedChildren}/${totalChildren}`}
                </Tag>
              </div>
            </>
          )}
        </div>

        {variant === 'error' && document.error && (
          <div className={styles.errorMessage} data-testid="document-import-card-error-message">
            {getErrorMessage(document.error.code, document.error.params)}
          </div>
        )}

        {variant === 'analyze-unavailable' && (
          <div
            className={styles.analyzeUnavailableMessage}
            data-testid="document-import-card-analyze-unavailable-message"
          >
            {t('documents.importStatus.card.analyzeUnavailableMessage')}
          </div>
        )}

        {hasChildren && (
          <>
            <div className={styles.childrenContainer} data-expanded={childrenExpanded}>
              <div className={styles.verticalLine} />
              <div className={styles.childrenList}>
                {document.children!.map((child, childIndex) => (
                  <DocumentImportCard
                    key={child.key || child.id}
                    document={child}
                    isChild
                    index={index + childIndex + 1}
                  />
                ))}
              </div>
            </div>

            <Button
              type="link"
              size="small"
              onClick={() => setChildrenExpanded(!childrenExpanded)}
              className={styles.toggleButton}
            >
              {childrenExpanded
                ? t('documents.importStatus.card.hideDetails')
                : t('documents.importStatus.card.showDetails')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
